import { Component, inject, signal, effect, computed } from '@angular/core';
import { NavigationEnd, Router, ActivatedRoute } from '@angular/router';
import { type MenuItem } from 'primeng/api';
import { Breadcrumb } from 'primeng/breadcrumb';
import { filter } from 'rxjs/operators';
import { BreadcrumbService } from '@app/core/breadcrumb.service';
import { WorkspaceService } from '@app/core/workspace.service';

@Component({
  selector: 'app-breadcrumb',
  standalone:true,
  imports: [Breadcrumb],
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss'
})
export class BreadcrumbComponent {
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  breadcrumbService = inject(BreadcrumbService);
  workspaceService = inject(WorkspaceService);

  home = computed<MenuItem>(() => {
    const workspaceId = this.workspaceService.activeWorkspaceId();
    if (workspaceId) {
      return { icon: 'pi pi-home', routerLink: ['/app', 'ws', workspaceId] };
    }
    return { icon: 'pi pi-home', routerLink: ['/app'] };
  });

  breadcrumbItems = signal<MenuItem[]>([]);

  constructor() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateBreadcrumbs();
      });
    
    // Initial breadcrumb generation
    setTimeout(() => this.updateBreadcrumbs(), 0);
    
    // React to dynamic label changes
    effect(() => {
      // Access the signal to trigger reactivity
      this.breadcrumbService.dynamicLabels();
      // Update breadcrumbs when labels change
      this.updateBreadcrumbs();
    });
  }

  private updateBreadcrumbs() {
    this.breadcrumbItems.set(this.createBreadcrumbs(this.activatedRoute.root));
  }

  createBreadcrumbs(route: ActivatedRoute, routerLink: string[] = [], breadcrumbs: MenuItem[] = []) {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    // Use the primary outlet route chain
    const child = children.find(c => c.outlet === 'primary') ?? children[0];

    const segment = child.snapshot.url.map(s => s.path).join('/');
    const nextRouterLink = [...routerLink];

    if (segment) {
      // Ensure absolute /app prefix is preserved
      if (segment === 'app') {
        nextRouterLink.length = 0;
        nextRouterLink.push('/app');
      } else {
        nextRouterLink.push(segment);
      }
    }

    let label = child.snapshot.data['breadcrumb'];
    
    // Check for dynamic label from BreadcrumbService
    if (label && segment) {
      const dynamicLabel = this.breadcrumbService.getLabel(segment);
      if (dynamicLabel) {
        label = dynamicLabel;
      }
      
      breadcrumbs.push({ label, routerLink: nextRouterLink });
    }

    return this.createBreadcrumbs(child, nextRouterLink, breadcrumbs);
  }
}
